import React, { useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { firstError, invalidClass, numberError, RULES, textError } from "../../../Helpers/bookingRules";
import { currencyFormatter } from "../../../Helpers/Utils";
import { openRazorpayCheckout } from "../../../Helpers/Razorpay";
import { ImCross } from "react-icons/im";
import tripUrls from "./tripUrls";

// Fine charges (Booking Flow v2, the last step of ending a trip; the booking must be Completed).
//   Calculate - POST <finesPreview>: the server prices every line (late hours, km over, FASTag, the
//               damage above the protection cover) and adds what is entered here (fuel, other fines,
//               goodwill discounts). Nothing is saved.
//   Submit    - PATCH <fines> with the same entries and expectedTotal (the total shown). The server
//               refuses if the charges changed since Calculate (Calculate again). Then:
//                 Closed  - charges within the security deposit; the balance is refunded.
//                 Pending - charges above the deposit; a payment link for the balance goes to the
//                           customer, with reminders. It can also be collected now by checkout.
// Late hours, km and FASTag can't be changed: a goodwill discount (with a reason) is the way to charge
// less, and needs permission in the staff panel.
//
// Resume: a Pending booking with an unpaid settlement order opens straight on the payment step.

const newRow = (fields) => ({ rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, ...fields });
const REASON_MAX = 200;

const copy = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.success("Payment link copied");
    } catch (error) {
        toast.error("Could not copy the link");
    }
};

const Line = ({ label, sub, amount, tone, strong }) => (
    <div className={`d-flex justify-content-between small mb-1 gap-3${strong ? " fw-semibold" : ""}${tone === "good" ? " text-success" : ""}`}>
        <span>{label}{sub ? <span className="text-muted"> {sub}</span> : null}</span>
        <span className="text-nowrap">{amount}</span>
    </div>
);

const Breakdown = ({ priced }) => {
    const { lines, subtotal, total, securityDeposit, action } = priced;
    return (
        <div className="border rounded-3 p-3 mb-3">
            <Line label="Late return" sub={lines.excessHour.quantity ? `${lines.excessHour.quantity} h x ${currencyFormatter(lines.excessHour.rate)}${lines.excessHour.lateMinutes ? `, ${lines.excessHour.lateMinutes} min late` : ""}` : "none"} amount={currencyFormatter(lines.excessHour.amount)} />
            <Line label="Km over the allowance" sub={lines.excessKm.quantity ? `${lines.excessKm.quantity} km = ${lines.excessKm.hourBlocks} h x ${currencyFormatter(lines.excessKm.rate)}` : "none"} amount={currencyFormatter(lines.excessKm.amount)} />
            <Line label="FASTag shortfall" sub={lines.excessFastag.quantity ? "" : "none"} amount={currencyFormatter(lines.excessFastag.amount)} />
            {lines.excessFuel.map((f) => <Line key={f.fuelType} label={`Fuel, ${f.fuelType}`} sub={`${f.percentage}% short`} amount={currencyFormatter(f.amount)} />)}
            {lines.damage.repairCost > 0 && (
                <Line label="Damage" sub={lines.damage.deductible === false ? "not charged" : `${currencyFormatter(lines.damage.repairCost)} repair - ${currencyFormatter(lines.damage.coverAmount)} protection cover`} amount={currencyFormatter(lines.damage.amount)} />
            )}
            {lines.misc.map((f, i) => <Line key={`misc${i}`} label={f.name} amount={currencyFormatter(f.amount)} />)}
            <hr className="my-2" />
            <Line label="Charges" amount={currencyFormatter(subtotal)} />
            {lines.discounts.map((d, i) => <Line key={`disc${i}`} label="Discount" sub={d.reason} amount={`- ${currencyFormatter(d.amount)}`} tone="good" />)}
            <Line label="Total to charge" amount={currencyFormatter(total)} strong />
            <Line label="Security deposit held" amount={currencyFormatter(securityDeposit)} />
            {action.type === "close"
                ? <div className="alert alert-success py-2 px-3 small mt-2 mb-0">Submit closes the booking and refunds {currencyFormatter(action.refund)} of the deposit.</div>
                : <div className="alert alert-warning py-2 px-3 small mt-2 mb-0">The charges are {currencyFormatter(action.due)} more than the deposit. Submit moves the booking to Pending and sends the customer a payment link for {currencyFormatter(action.due)}.</div>}
        </div>
    );
};

const InspectModal = ({ bookingId, customerName, customerPhone, prevSettlementOrder, settlementLink, excess, canDiscount = true, canSettle = true, onClose, onInspected }) => {
    const fuelExcess = excess?.fuel || [];
    const [fuelFines, setFuelFines] = useState({});
    const [fineRows, setFineRows] = useState([]);
    const [discountRows, setDiscountRows] = useState([]);
    const [errors, setErrors] = useState({});
    const [priced, setPriced] = useState(null); // the Calculate result; Submit is offered only with it
    const [busy, setBusy] = useState(false);
    const [paying, setPaying] = useState(false);
    // After Submit (or when resuming a Pending booking): { bookingStage, fineTotal, refundAmount?, shortfall?, orderId?, paymentLink? }
    const [result, setResult] = useState(prevSettlementOrder ? { bookingStage: "Pending", shortfall: prevSettlementOrder.totalAmount, orderId: prevSettlementOrder.orderId, paymentLink: settlementLink || null, resumed: true } : null);

    const changed = () => setPriced(null);
    const filledFines = fineRows.filter((r) => r.name.trim() || r.amount !== "");
    const filledDiscounts = discountRows.filter((r) => r.reason.trim() || r.amount !== "");

    const validate = () => {
        const fuel = {};
        fuelExcess.forEach((r) => {
            const msg = numberError(fuelFines[r.fuelType] ?? "", { label: `${r.fuelType} charge`, min: 0, max: RULES.MONEY_MAX, maxDecimals: 2 });
            if (msg) fuel[r.fuelType] = msg;
        });
        const fines = {};
        filledFines.forEach((r) => {
            const msg = textError(r.name, { label: "Description", required: true, min: RULES.FINE_NAME_MIN, max: RULES.FINE_NAME_MAX })
                || numberError(r.amount, { label: "Amount", required: true, gt: 0, max: RULES.MONEY_MAX, maxDecimals: 2 });
            if (msg) fines[r.rowId] = msg;
        });
        const discounts = {};
        filledDiscounts.forEach((r) => {
            const msg = numberError(r.amount, { label: "Discount", required: true, gt: 0, max: RULES.MONEY_MAX, maxDecimals: 2 })
                || textError(r.reason, { label: "Reason", required: true, min: 3, max: REASON_MAX });
            if (msg) discounts[r.rowId] = msg;
        });
        const next = { fuel, fines, discounts };
        setErrors(next);
        return firstError(next);
    };

    const payload = () => ({
        excessFuel: fuelExcess.filter((r) => (fuelFines[r.fuelType] ?? "") !== "").map((r) => ({ fuelType: r.fuelType, percentage: r.percentage, amount: Number(fuelFines[r.fuelType]) })),
        fines: filledFines.map((r) => ({ name: r.name.trim(), amount: Number(r.amount) })),
        ...(canDiscount && filledDiscounts.length ? { discounts: filledDiscounts.map((r) => ({ amount: Number(r.amount), reason: r.reason.trim() })) } : {}),
    });

    const calculate = async () => {
        const problem = validate();
        if (problem) { toast.error(problem); return; }
        setBusy(true);
        const response = await Handler({ method: "post", url: tripUrls.finesPreview(bookingId), data: payload() });
        setBusy(false);
        if (!response?.success || !response?.data?.data?.lines) {
            toast.error(response?.data?.message || "Could not work out the charges");
            return;
        }
        setPriced(response.data.data);
    };

    const submit = async () => {
        setBusy(true);
        const response = await Handler({ method: "patch", url: tripUrls.fines(bookingId), data: { ...payload(), expectedTotal: priced.total } });
        setBusy(false);
        if (!response?.success || !response?.data?.data) {
            const message = response?.data?.message || "Could not submit the fine charges";
            toast.error(message);
            if (/changed since/i.test(message)) setPriced(null); // Calculate again
            return;
        }
        setResult(response.data.data);
        onInspected?.();
    };

    const finishPayment = async (checkoutResult) => {
        if (["dismissed", "failed", "error"].includes(checkoutResult.status)) {
            if (checkoutResult.status === "failed") toast.error("Payment failed. You can try again.");
            if (checkoutResult.status === "error") toast.error(checkoutResult.message);
            setPaying(false);
            return;
        }
        const razorpayResponse = checkoutResult.response;
        const verify = await Handler({ method: "patch", url: tripUrls.pendingVerify(bookingId), data: { paymentId: razorpayResponse.razorpay_payment_id, signature: razorpayResponse.razorpay_signature } });
        setPaying(false);
        if (verify?.success) {
            toast.success("Balance collected, booking closed");
            onInspected?.();
            onClose?.();
        } else {
            toast.error(verify?.data?.message || `Payment received but couldn't be applied. Contact support with payment ID ${razorpayResponse.razorpay_payment_id}.`);
        }
    };

    const collectNow = async () => {
        setPaying(true);
        const checkout = await openRazorpayCheckout({
            bookingId,
            orderId: result.orderId,
            amount: result.shortfall,
            description: `Booking ${bookingId} balance after the trip`,
            prefill: { name: customerName, contact: customerPhone },
        });
        await finishPayment(checkout);
    };

    const disabled = busy || paying;
    const rowError = (key, rowId) => errors[key]?.[rowId];
    const clearRow = (key, rowId) => setErrors((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [rowId]: null } }));

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{result?.bookingStage === "Pending" ? "Balance Due" : "Fine Charges"}</h5>
                            <button type="button" className="btn-close" onClick={onClose} disabled={disabled}></button>
                        </div>
                        <div className="modal-body">
                            {result ? (
                                result.bookingStage === "Closed" ? (
                                    <div className="border rounded-3 p-3">
                                        <Line label="Total charged" amount={currencyFormatter(result.fineTotal ?? 0)} />
                                        <Line label="Deposit refunded" amount={currencyFormatter(result.refundAmount ?? 0)} strong />
                                        <div className="alert alert-success py-2 px-3 small mt-2 mb-0">The charges were within the security deposit. The balance was refunded and the booking is Closed.</div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="alert alert-warning py-2 px-3 small">
                                            {result.resumed ? "This booking is Pending." : "Charges submitted. The booking is Pending."} The customer owes <strong>{currencyFormatter(result.shortfall ?? 0)}</strong>
                                            {result.paymentLink ? " and has been sent a payment link (reminders follow until it is paid)." : "."}
                                        </div>
                                        {result.paymentLink && (
                                            <div className="input-group input-group-sm mb-3">
                                                <input className="form-control" readOnly value={result.paymentLink} aria-label="Payment link" />
                                                <button type="button" className="btn btn-outline-secondary" onClick={() => copy(result.paymentLink)}>Copy link</button>
                                            </div>
                                        )}
                                        {canSettle && result.orderId && (
                                            <button type="button" className="btn btn-success w-100" onClick={collectNow} disabled={paying}>
                                                {paying ? "Opening payment..." : `Collect ${currencyFormatter(result.shortfall ?? 0)} now by checkout`}
                                            </button>
                                        )}
                                    </>
                                )
                            ) : (
                                <>
                                    <div className="small text-muted mb-3">
                                        Late hours, km over and FASTag are priced by the server from what was recorded; damage is the repair cost above the trip protection cover. Enter the fuel charges and any other fines{canDiscount ? ", and a discount if one is agreed" : ""}, then Calculate.
                                    </div>

                                    {fuelExcess.length > 0 && (
                                        <div className="mb-3">
                                            <div className="fw-semibold small mb-2">Fuel short at return</div>
                                            {fuelExcess.map((row) => (
                                                <div key={row.fuelType} className="row g-2 align-items-center mb-2">
                                                    <div className="col-6 small">{row.fuelType}<div className="text-muted"><strong>{row.percentage}%</strong> lower than at handover</div></div>
                                                    <div className="col-6">
                                                        <NumberField decimals={2} min={0} max={RULES.MONEY_MAX} className={`form-control form-control-sm${invalidClass(errors.fuel?.[row.fuelType])}`} placeholder="Charge ₹ (blank = none)" aria-label={`${row.fuelType} fuel charge`} value={fuelFines[row.fuelType] ?? ""} onChange={(v) => { setFuelFines((prev) => ({ ...prev, [row.fuelType]: v })); clearRow("fuel", row.fuelType); changed(); }} disabled={disabled} />
                                                        {errors.fuel?.[row.fuelType] && <div className="text-danger small" role="alert">{errors.fuel[row.fuelType]}</div>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="fw-semibold small">Other fines</span>
                                            <button type="button" className="btn btn-sm btn-link p-0" onClick={() => { setFineRows((prev) => [...prev, newRow({ name: "", amount: "" })]); changed(); }} disabled={disabled || fineRows.length >= RULES.FINE_ROWS_MAX}>+ Add</button>
                                        </div>
                                        {fineRows.map((row) => (
                                            <div key={row.rowId} className="row g-2 align-items-center mb-2">
                                                <div className="col-7">
                                                    <input type="text" className="form-control form-control-sm" placeholder="Description" maxLength={RULES.FINE_NAME_MAX} value={row.name} onChange={(e) => { setFineRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, name: e.target.value } : r))); clearRow("fines", row.rowId); changed(); }} disabled={disabled} />
                                                </div>
                                                <div className="col-4">
                                                    <NumberField decimals={2} min={0} max={RULES.MONEY_MAX} className="form-control form-control-sm" placeholder="Amount ₹" value={row.amount} onChange={(v) => { setFineRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, amount: v } : r))); clearRow("fines", row.rowId); changed(); }} disabled={disabled} />
                                                </div>
                                                <div className="col-1">
                                                    <button type="button" className="btn btn-sm btn-outline-danger p-1" onClick={() => { setFineRows((prev) => prev.filter((r) => r.rowId !== row.rowId)); changed(); }} disabled={disabled} aria-label="Remove fine"><ImCross size={8} /></button>
                                                </div>
                                                {rowError("fines", row.rowId) && <div className="col-12 text-danger small" role="alert">{rowError("fines", row.rowId)}</div>}
                                            </div>
                                        ))}
                                    </div>

                                    {canDiscount && (
                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-semibold small">Goodwill discount</span>
                                                <button type="button" className="btn btn-sm btn-link p-0" onClick={() => { setDiscountRows((prev) => [...prev, newRow({ amount: "", reason: "" })]); changed(); }} disabled={disabled || discountRows.length >= 5}>+ Add</button>
                                            </div>
                                            {discountRows.map((row) => (
                                                <div key={row.rowId} className="row g-2 align-items-center mb-2">
                                                    <div className="col-4">
                                                        <NumberField decimals={2} min={0} max={RULES.MONEY_MAX} className="form-control form-control-sm" placeholder="Amount ₹" value={row.amount} onChange={(v) => { setDiscountRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, amount: v } : r))); clearRow("discounts", row.rowId); changed(); }} disabled={disabled} />
                                                    </div>
                                                    <div className="col-7">
                                                        <input type="text" className="form-control form-control-sm" placeholder="Reason (shown on the customer's receipt)" maxLength={REASON_MAX} value={row.reason} onChange={(e) => { setDiscountRows((prev) => prev.map((r) => (r.rowId === row.rowId ? { ...r, reason: e.target.value } : r))); clearRow("discounts", row.rowId); changed(); }} disabled={disabled} />
                                                    </div>
                                                    <div className="col-1">
                                                        <button type="button" className="btn btn-sm btn-outline-danger p-1" onClick={() => { setDiscountRows((prev) => prev.filter((r) => r.rowId !== row.rowId)); changed(); }} disabled={disabled} aria-label="Remove discount"><ImCross size={8} /></button>
                                                    </div>
                                                    {rowError("discounts", row.rowId) && <div className="col-12 text-danger small" role="alert">{rowError("discounts", row.rowId)}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {priced && <Breakdown priced={priced} />}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={disabled}>{result ? "Close" : "Cancel"}</button>
                            {!result && (priced
                                ? <button type="button" className="btn btn-danger" onClick={submit} disabled={disabled}>{busy ? "Submitting..." : `Submit ${currencyFormatter(priced.total)}`}</button>
                                : <button type="button" className="btn btn-primary" onClick={calculate} disabled={disabled}>{busy ? "Calculating..." : "Calculate"}</button>)}
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default InspectModal;
